import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  inject,
  Output,
} from '@angular/core';
import { concatMap, from } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { Optional } from '@src/types/utils';
import { AlertService } from '@src/core/services';
import { EnvService } from '@src/app/modules/env';

import { StoreService } from '../../services';
import { AttachmentItems } from '../../attachment';
import { MAX_MESSAGE_LENGTH } from '../../constants';
import { MessageService } from '../../message';
import { splitMessage } from '../../utils';

@Component({
  selector: 'chat-input-box',
  templateUrl: './chat-input-box.component.html',
  styleUrls: ['./chat-input-box.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class ChatInputBoxComponent {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly messageService = inject(MessageService);
  private readonly store = inject(StoreService);
  private readonly alertService = inject(AlertService);
  private readonly translateService = inject(TranslateService);
  private readonly env = inject(EnvService);

  @Input() chatId!: string;
  @Output() textMessageSent = new EventEmitter<void>();

  isInputAreaInvalid = false;

  get footerMessage() {
    return this.store.getFooterMessage(this.chatId);
  }

  get newMessage() {
    return this.store.getDraftMessage(this.chatId);
  }

  set newMessage(value: string) {
    this.store.setDraftMessageState(this.chatId, value);

    if (this.newMessage && this.footerMessage?.mode === 'edit') {
      this.isInputAreaInvalid = this.trimHtml(this.newMessage).length > MAX_MESSAGE_LENGTH;
    }
  }

  get isMobile() {
    return this.env.isMobilePlatform;
  }

  sendTextMessage() {
    const normalizeNewMessage = this.trimHtml(this.newMessage);
    if (!normalizeNewMessage || !this.chatId) {
      return;
    }

    let editMessageId: Optional<string> = undefined;

    if (this.footerMessage?.mode === 'edit') {
      if (normalizeNewMessage.length > MAX_MESSAGE_LENGTH) {
        this.alertService.error(
          this.translateService.instant('components.internalMessenger.alerts.errors.maxMessageLength'),
        );
        return;
      }

      editMessageId = this.footerMessage.message.id;

      return this.messageService.editTextMessage(this.chatId, editMessageId, normalizeNewMessage).subscribe(() => {
        this.newMessage = '';
        this.store.deleteFooterMessage(this.chatId);
        this.cdr.markForCheck();
      });
    }

    let replyMessageId: Optional<string> = undefined;

    if (this.footerMessage?.mode === 'reply') {
      replyMessageId = this.footerMessage.message.id;
    }

    const normalizeNewMessageArray = splitMessage(normalizeNewMessage, MAX_MESSAGE_LENGTH);
    return from(normalizeNewMessageArray)
      .pipe(
        concatMap(message => {
          // TODO: добавить replyMessageText
          return this.messageService.sendTextMessage(this.chatId, message, replyMessageId);
        }),
      )
      .subscribe(() => {
        this.newMessage = '';
        this.store.deleteFooterMessage(this.chatId);
        this.textMessageSent.emit();
        this.cdr.markForCheck();
      });
  }

  sendAttachments(data: AttachmentItems) {
    if (!this.chatId) {
      return;
    }

    let replyMessageId: Optional<string> = undefined;

    if (this.footerMessage?.mode === 'reply') {
      replyMessageId = this.footerMessage.message.id;
    }

    const { attachments, type, caption } = data;

    let normalizeNewMessage: string | undefined;
    if (caption) {
      normalizeNewMessage = this.trimHtml(caption);
    }

    this.messageService
      .sendAttachmentsMessage(this.chatId, attachments, type, normalizeNewMessage, replyMessageId)
      .subscribe(() => {
        this.newMessage = '';
        this.store.deleteFooterMessage(this.chatId);
      });
  }

  insertEmoji(emojiName: string | null): void {
    // this.editor
    //   ?.getOriginTiptapEditor()
    //   ?.chain()
    //   ?.focus()
    //   ?.insertContent(`<p data-type="emoji">${emoji}</p>`)
    //   ?.insertContent(' ')
    //   ?.run();
  }

  private trimHtml(html: string) {
    const result = html
      .replace(new RegExp('</p><p>', 'g'), '<br>')
      .replace(new RegExp('<p></p>', 'g'), '<br>')
      .replace(new RegExp('<p>', 'g'), '')
      .replace(new RegExp('</p>', 'g'), '')
      .replace(new RegExp('^(<br>)+', 'g'), '')
      .replace(new RegExp('(<br>)+$', 'g'), '')
      .trim();

    return result;
  }
}
